import { GoogleGenerativeAI } from "@google/generative-ai"
import OpenAI from "openai"
import { storeData, getStoredData } from "@/controllers/BaseController"
import { loadConfig } from "@/configLoader"
import moment from "moment"
import { htmlToText } from "html-to-text"

const GEMINI_API_KEY = "AIzaSyDd4cSNFiWgg8vvVaeLkQm9fE29Ii91V5g"
const genAI = new GoogleGenerativeAI(GEMINI_API_KEY)
const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash-exp" })

let openai = null

async function initializeOpenAI() {
  if (openai) return openai
  try {
    const config = await loadConfig()
    openai = new OpenAI({
      apiKey: config.openAiAPI,
      dangerouslyAllowBrowser: true
    })
    return openai
  } catch (error) {
    console.error("Failed to initialize OpenAI:", error)
    return null
  }
}

const SUPPORTED_LANGUAGES = { en: "English", de: "German" }
const CACHE_DURATION_HOURS = 24
const MAX_PARALLEL_CALLS = 6
const ITEMS_PER_BATCH = 12

function isHtmlContent(text) {
  return text && typeof text === "string" && /<[^>]+>/.test(text)
}

function htmlToPlainText(html) {
  if (!isHtmlContent(html)) return html
  return htmlToText(html, {
    wordwrap: false,
    preserveNewlines: true,
    selectors: [
      { selector: "a", format: "inline" },
      { selector: "img", format: "skip" },
      { selector: "p", options: { leadingLineBreaks: 1, trailingLineBreaks: 1 } }
    ]
  }).trim()
}

function plainTextToHtml(plainText) {
  const paragraphs = plainText.split(/\n\n+/).filter(p => p.trim())
  if (paragraphs.length === 0) return `<p>${plainText}</p>`
  return paragraphs.map(p => `<p>${p.trim()}</p>`).join("\n")
}

function quickDetectLanguage(text) {
  const plainText = isHtmlContent(text) ? htmlToPlainText(text) : text
  const lowerText = plainText.toLowerCase()
  const germanWords = /\b(der|die|das|und|oder|ist|sind|werden|wurde|über|für|mit|von|zu|auf|auch|noch|mehr)\b/gi
  const germanMatches = (lowerText.match(germanWords) || []).length
  const englishWords = /\b(the|and|or|is|are|was|were|will|have|has|for|with|from|to|on|at|been|more)\b/gi
  const englishMatches = (lowerText.match(englishWords) || []).length
  return germanMatches > englishMatches && germanMatches > 2 ? "de" : "en"
}

function generateCacheKey(content, targetLang) {
  const contentStr = typeof content === "string" ? content : JSON.stringify(content)
  const hash = contentStr.substring(0, 100).replace(/[^a-zA-Z0-9]/g, "")
  return `translation_${targetLang}_${hash}`
}

function splitIntoBatches(items, batchSize = ITEMS_PER_BATCH) {
  const batches = []
  for (let i = 0; i < items.length; i += batchSize) {
    batches.push(items.slice(i, i + batchSize))
  }
  return batches
}

function parseTranslations(translatedText) {
  const translations = []
  const itemPattern = /(\d+)\.\s*([\s\S]*?)(?=\n\d+\.|$)/g
  let match
  while ((match = itemPattern.exec(translatedText)) !== null) {
    translations.push(match[2].trim())
  }
  if (translations.length === 0) {
    const lines = translatedText.split(/\n\d+\.\s+/).filter(line => line.trim())
    translations.push(...lines.map(line => line.trim()))
  }
  return translations
}

async function translateSingleBatch(batch, targetLang, batchIndex, useGemini = true) {
  const targetLanguage = SUPPORTED_LANGUAGES[targetLang]
  const numberedItems = batch.map((item, idx) => `${idx + 1}. ${item}`).join("\n\n")

  const prompt = `Translate to ${targetLanguage}. If already in ${targetLanguage}, return unchanged. Return numbered translations.

${numberedItems}`

  try {
    if (useGemini) {
      console.log(`Batch ${batchIndex + 1}: Gemini translating ${batch.length} items`)
      const result = await model.generateContent(prompt)
      const translatedText = result.response.text().trim()
      if (!translatedText) throw new Error("Empty Gemini response")
      return { success: true, translations: parseTranslations(translatedText), batchIndex }
    } else {
      const openaiClient = await initializeOpenAI()
      if (!openaiClient) throw new Error("OpenAI not initialized")

      console.log(`Batch ${batchIndex + 1}: OpenAI translating ${batch.length} items`)
      const response = await openaiClient.chat.completions.create({
        model: "gpt-4o-mini",
        messages: [
          { role: "system", content: `Translate to ${targetLanguage}. Return numbered translations.` },
          { role: "user", content: numberedItems }
        ],
        temperature: 0.3,
        max_tokens: 4000
      })

      const translatedText = response.choices[0]?.message?.content?.trim()
      if (!translatedText) throw new Error("Empty OpenAI response")
      return { success: true, translations: parseTranslations(translatedText), batchIndex }
    }
  } catch (error) {
    console.error(`Batch ${batchIndex + 1} failed (${useGemini ? "Gemini" : "OpenAI"}):`, error.message)
    return { success: false, originalBatch: batch, batchIndex, error: error.message }
  }
}

async function translateWithRetry(batch, targetLang, batchIndex, useGemini = true) {
  const firstAttempt = await translateSingleBatch(batch, targetLang, batchIndex, useGemini)

  if (firstAttempt.success) {
    return firstAttempt
  }

  console.log(`Batch ${batchIndex + 1}: Retrying with ${useGemini ? "OpenAI" : "Gemini"}`)
  const secondAttempt = await translateSingleBatch(batch, targetLang, batchIndex, !useGemini)

  if (secondAttempt.success) {
    return secondAttempt
  }

  console.error(`Batch ${batchIndex + 1}: Both APIs failed, keeping original`)
  return { success: false, originalBatch: batch, batchIndex }
}

async function batchTranslateWithLoadBalancing(items, targetLang) {
  const batches = splitIntoBatches(items, ITEMS_PER_BATCH)
  const numBatches = Math.min(batches.length, MAX_PARALLEL_CALLS)

  console.log(`Translating ${items.length} items in ${numBatches} parallel batches (load balanced)`)

  const batchPromises = batches.slice(0, numBatches).map((batch, index) => {
    const useGemini = index % 2 === 0
    return translateWithRetry(batch, targetLang, index, useGemini)
  })

  const results = await Promise.all(batchPromises)

  const allTranslations = []
  let successCount = 0
  let failCount = 0

  results.forEach(result => {
    if (result.success) {
      allTranslations.push(...result.translations)
      successCount++
    } else {
      allTranslations.push(...result.originalBatch)
      failCount++
    }
  })

  console.log(`Translation complete: ${successCount} batches succeeded, ${failCount} batches failed (kept original)`)
  return allTranslations
}

export async function translateContent(content, targetLang = "en", keysToTranslate = null) {
  if (!["en", "de"].includes(targetLang)) {
    console.warn(`Unsupported language: ${targetLang}. Defaulting to 'en'`)
    targetLang = "en"
  }

  try {
    if (typeof content === "string") {
      if (!content.trim()) return content

      const sourceLang = quickDetectLanguage(content)
      if (sourceLang === targetLang) {
        console.log("Already in target language, skipping translation")
        return content
      }

      const cacheKey = generateCacheKey(content, targetLang)
      const cached = await getStoredData(cacheKey)

      if (cached && moment().diff(moment(cached.timestamp), "hours") < CACHE_DURATION_HOURS) {
        console.log("Using cached translation")
        return cached.translation
      }

      const wasHtml = isHtmlContent(content)
      const plainText = wasHtml ? htmlToPlainText(content) : content

      const translations = await batchTranslateWithLoadBalancing([plainText], targetLang)
      let translation = translations[0]

      if (wasHtml) {
        translation = plainTextToHtml(translation)
      }

      await storeData(cacheKey, {
        translation,
        timestamp: moment().toISOString()
      })

      return translation
    } else if (Array.isArray(content) && content.length > 0 && typeof content[0] === "string") {
      const nonEmptyTexts = content.filter(text => text && text.trim())
      if (nonEmptyTexts.length === 0) return content

      const sourceLang = quickDetectLanguage(nonEmptyTexts[0])
      if (sourceLang === targetLang) return content

      return await batchTranslateWithLoadBalancing(nonEmptyTexts, targetLang)
    } else if (Array.isArray(content) && content.length > 0 && typeof content[0] === "object") {
      const keys = keysToTranslate || Object.keys(content[0]).filter(k => typeof content[0][k] === "string")

      const allTexts = []
      const textMap = []
      const htmlFlags = []

      content.forEach((obj, objIndex) => {
        keys.forEach(key => {
          if (key in obj && typeof obj[key] === "string" && obj[key].trim()) {
            const wasHtml = isHtmlContent(obj[key])
            const plainText = wasHtml ? htmlToPlainText(obj[key]) : obj[key]

            allTexts.push(plainText)
            textMap.push({ objIndex, key })
            htmlFlags.push(wasHtml)
          }
        })
      })

      if (allTexts.length === 0) return content

      const sourceLang = quickDetectLanguage(allTexts[0])
      if (sourceLang === targetLang) {
        console.log("Content already in target language, skipping translation")
        return content
      }

      const translations = await batchTranslateWithLoadBalancing(allTexts, targetLang)

      const result = content.map(obj => ({ ...obj }))

      translations.forEach((translation, idx) => {
        const { objIndex, key } = textMap[idx]
        const wasHtml = htmlFlags[idx]
        result[objIndex][key] = wasHtml ? plainTextToHtml(translation) : translation
      })

      return result
    } else if (typeof content === "object" && content !== null) {
      const keys = keysToTranslate || Object.keys(content).filter(k => typeof content[k] === "string")

      const textsToTranslate = []
      const htmlFlags = []

      keys.forEach(key => {
        if (key in content && typeof content[key] === "string" && content[key].trim()) {
          const wasHtml = isHtmlContent(content[key])
          const plainText = wasHtml ? htmlToPlainText(content[key]) : content[key]
          textsToTranslate.push(plainText)
          htmlFlags.push(wasHtml)
        }
      })

      if (textsToTranslate.length === 0) return content

      const sourceLang = quickDetectLanguage(textsToTranslate[0])
      if (sourceLang === targetLang) return content

      const translations = await batchTranslateWithLoadBalancing(textsToTranslate, targetLang)

      const result = { ...content }
      let translationIndex = 0
      keys.forEach(key => {
        if (key in content && typeof content[key] === "string" && content[key].trim()) {
          const translation = translations[translationIndex]
          const wasHtml = htmlFlags[translationIndex]
          result[key] = wasHtml ? plainTextToHtml(translation) : translation
          translationIndex++
        }
      })

      return result
    }

    return content
  } catch (error) {
    console.error("Translation process failed, returning original:", error.message)
    return content
  }
}
